/**
 * Shared helpers for VSC report forms.
 */
import { toLocalDateString } from '@/utils/format-date-utils';
import type {
  CreateVscReportDto,
  CreateVscReportFormState,
  CreateVscReportResult,
  UpdateVscReportDto,
  VscReport,
} from '@/types/vsc-report.types';
import { getErrorMessage } from '@/utils/errors-utils';

/** Empty create form with today's local date pre-filled. */
export function emptyCreateVscReportForm(): CreateVscReportFormState {
  return {
    establishmentId: '',
    reportDate: toLocalDateString(new Date()),
  };
}

/** Immutable patch of one create-form field. */
export function applyVscReportFormField(
  form: CreateVscReportFormState,
  field: keyof CreateVscReportFormState,
  value: string,
): CreateVscReportFormState {
  return { ...form, [field]: value };
}

/** Both establishment and report date are required. */
export function isCreateVscReportFormValid(form: CreateVscReportFormState): boolean {
  return !!form.establishmentId && !!form.reportDate;
}

/** Map the create form to the batch-generate API body. */
export function buildCreateVscReportDto(form: CreateVscReportFormState): CreateVscReportDto {
  return {
    establishmentId: form.establishmentId,
    reportDate: form.reportDate,
  };
}

/** Success toast — plural when more than one report was created. */
export function formatCreateVscReportSuccess(created: number): string {
  if (created > 1) return `${created} rapports VSC générés`;
  return 'Rapport VSC généré';
}

/** Info toast when every volunteer already has a report for that date. */
export function formatCreateVscReportAlreadyExists(existing: number): string {
  if (existing > 1) return getErrorMessage('vscReportsAlreadyExist');
  return getErrorMessage('vscReportAlreadyExists');
}

/** `alreadyExists` when `created === 0`; otherwise a created-count success. */
export function resolveCreateVscReportOutcome(result: CreateVscReportResult): {
  kind: 'alreadyExists' | 'created';
  message: string;
} {
  if (result.created === 0) {
    return {
      kind: 'alreadyExists',
      message: formatCreateVscReportAlreadyExists(result.existing),
    };
  }
  return {
    kind: 'created',
    message: formatCreateVscReportSuccess(result.created),
  };
}

/** Central error copy from {@link getErrorMessage}. */
export function getVscReportFormErrorMessage(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** Rating + comment draft; empty comment becomes `''`. */
export function toDetailDraft(report: Pick<VscReport, 'rating' | 'comment'>): {
  rating: number | null;
  comment: string;
} {
  return { rating: report.rating, comment: report.comment ?? '' };
}

/** Submit DTO: blank comment → `null`, status `submitted`. */
export function buildSubmitDto(draft: {
  rating: number | null;
  comment: string;
}): UpdateVscReportDto {
  return { rating: draft.rating, comment: draft.comment || null, status: 'submitted' };
}
